import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, Info } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

type SourceMeta = { slug: string; name: string; category: string };

// Module-level cache so we don't refetch per card.
let cache: SourceMeta[] | null = null;
let inflight: Promise<SourceMeta[]> | null = null;

async function loadSources(): Promise<SourceMeta[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase
      .from("trend_sources")
      .select("slug, name, category");
    cache = (data as SourceMeta[]) ?? [];
    inflight = null;
    return cache;
  })();
  return inflight;
}

export function SourceTransparency({
  sourceSlugs,
  signalCount,
}: {
  sourceSlugs: string[];
  signalCount?: number;
}) {
  const [sources, setSources] = useState<SourceMeta[] | null>(cache);

  useEffect(() => {
    if (!cache) loadSources().then(setSources);
  }, []);

  if (!sourceSlugs?.length) return null;

  const matched = (sources ?? []).filter((s) => sourceSlugs.includes(s.slug));
  const categories = Array.from(
    new Set(matched.map((s) => s.category).filter(Boolean)),
  );
  // Fall back to slugs we don't have metadata for yet.
  const fallback = sourceSlugs.filter((sl) => !matched.some((m) => m.slug === sl));

  return (
    <div className="mt-3 rounded-lg border border-border/60 bg-secondary/20 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground">
          <ShieldCheck className="size-3 text-primary" />
          source transparency
          {typeof signalCount === "number" && (
            <span className="text-muted-foreground/70"> · {signalCount} signals</span>
          )}
        </div>
        <HoverCard>
          <HoverCardTrigger asChild>
            <button
              aria-label="What this means"
              className="text-muted-foreground hover:text-foreground transition"
            >
              <Info className="size-3" />
            </button>
          </HoverCardTrigger>
          <HoverCardContent className="text-xs w-72">
            We surface the <b>kinds</b> of sources that contributed to this trend
            so you can validate it — without revealing the proprietary scanning
            methods that power Trends Discovery.
          </HoverCardContent>
        </HoverCard>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {categories.map((c) => (
          <span
            key={c}
            className="font-mono text-[10px] rounded-full border border-primary/40 bg-primary/5 text-primary px-2 py-0.5"
            title={
              matched
                .filter((m) => m.category === c)
                .map((m) => m.name)
                .join(", ")
            }
          >
            {c}
          </span>
        ))}
        {fallback.map((slug) => (
          <span
            key={slug}
            className="font-mono text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground"
          >
            {slug}
          </span>
        ))}
      </div>
    </div>
  );
}
